import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import EdgeIncidentsTab from "./EdgeIncidentsTab";
import { alarmFieldLabel } from "../utils/alarmLabels";
import { getEdgeById, setEdgeCloudTransfer, issueDeviceClaim, getEdgeAlarmEvents, ackAlarmEvent } from "../api/edgeApi";
import { changeEdgeTier } from "../api/capabilityApi";
import { hasCap, apiErrorMessage } from "../utils/capabilities";
import { deleteEdge } from "../api/tenantApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { formatDate } from "../utils/i18nFormat";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Typography } from "@mui/material";
import OtaCard from "../components/EdgeExplorer/OtaCard";
import MacList from "../components/EdgeExplorer/MacList";
import AssetsTab from "../components/EdgeExplorer/AssetsTab";
import { certInfo } from "../components/EdgeExplorer/otaHelpers";
import EdgeEditDialog from "../components/EdgeExplorer/EdgeEditDialog";
import MoveEdgeDialog from "../components/EdgeExplorer/MoveEdgeDialog";
import TransferEdgeDialog from "../components/EdgeExplorer/TransferEdgeDialog";
import {
  getDashboardEdgeSystem,
  getDashboardEdgeDevices,
  getDashboardDataLatest,
} from "../api/dashboardApi";

// Seconds-precision stamp like the former toLocaleString("tr-TR"); follows the selected language.
const STAMP = { dateStyle: "short", timeStyle: "medium" };

export default function EdgeDetail() {
  const { t } = useTranslation();
  const { edgeDbId } = useParams();
  const navigate = useNavigate();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const isSuper = me?.global_role === "superadmin";
  const canEditEdge = me?.global_role === "superadmin" || me?.tenant_role === "admin";
  const canTransfer = hasCap(me, "edge.transfer");
  const canChangeTier = hasCap(me, "edge.tier.change");

  const [edge, setEdge] = useState(null);
  const [cloudTransferEnabled, setCloudTransferEnabled] = useState(false);
  // Entitlement assigned by VALONIX (superadmin). A tenant admin may switch transfer off/on only while it is granted.
  const cloudTransferAllowed = Boolean(edge?.cloud_transfer_allowed);
  const canToggleCloudTransfer = canEditEdge && (isSuper || cloudTransferAllowed);
  const [savingCloudTransfer, setSavingCloudTransfer] = useState(false);
  const [tier, setTier] = useState("pro");
  const [savingTier, setSavingTier] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sensor_latest");
  const [sensorRows, setSensorRows] = useState([]);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLoading, setErrorLoading] = useState(false);
  const [alarms, setAlarms] = useState([]);
  const [alarmsLoading, setAlarmsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const claimRequest = useRef(0);
  const [claim, setClaim] = useState(null); // single-use code issued for re-enrollment

  const handleReclaim = async () => {
    const requestedId = edgeDbId;
    const requestId = ++claimRequest.current;
    setClaiming(true);
    try {
      const next = await issueDeviceClaim(accessToken, requestedId);
      if (requestId !== claimRequest.current) return;
      if (next?.edge_uuid !== requestedId) {
        toast.error(t("edgeDetail.detail.claimWrongEdge"));
        return;
      }
      setClaim(next);
    } catch (err) {
      if (requestId !== claimRequest.current) return;
      toast.error(err?.response?.data?.detail || t("edgeDetail.detail.claimFailed"));
    } finally {
      if (requestId === claimRequest.current) setClaiming(false);
    }
  };

  const fetchEdge = useCallback(async () => {
    if (!accessToken || !edgeDbId) return;
    try {
      setLoading(true);
      const data = await getEdgeById(accessToken, edgeDbId);
      setEdge(data);
      setCloudTransferEnabled(Boolean(data.cloud_transfer_enabled));
      setTier((data.tier || "pro").toLowerCase() === "lite" ? "lite" : "pro");
    } catch (err) {
      toast.error(err.response?.data?.detail || i18n.t("edgeDetail.detail.notFound"));
    } finally {
      setLoading(false);
    }
  }, [accessToken, edgeDbId]);

  const handleDeleteEdge = async () => {
    if (!window.confirm(t("edgeDetail.detail.deleteConfirm", { name: edge?.name ?? edge?.edgeId }))) return;
    setDeleting(true);
    try {
      await deleteEdge(accessToken, edgeDbId);
      toast.success(t("edgeDetail.detail.deleted", { name: edge?.name ?? edge?.edgeId }));
      navigate("/edges");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("edgeDetail.detail.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchEdge();
  }, [fetchEdge]);

  const fetchSensorLatest = useCallback(async () => {
    if (!accessToken || !edge?.edgeId) return;
    setSensorLoading(true);
    try {
      const devices = await getDashboardEdgeDevices(accessToken, edge.edgeId);
      const deviceIds = (devices || []).map((d) => d.id);
      if (!deviceIds.length) { setSensorRows([]); return; }
      const latest = await getDashboardDataLatest(accessToken, edge.edgeId, deviceIds);
      setSensorRows(Array.isArray(latest) ? latest : []);
    } catch (_) {
      setSensorRows([]);
    } finally {
      setSensorLoading(false);
    }
  }, [accessToken, edge?.edgeId]);

  const fetchErrorLogs = useCallback(async () => {
    if (!accessToken || !edge?.edgeId) return;
    setErrorLoading(true);
    try {
      const record = await getDashboardEdgeSystem(accessToken, edge.edgeId);
      let errors = {};
      try {
        if (record?.device_errors && record.device_errors !== "{}") {
          errors = typeof record.device_errors === "string"
            ? JSON.parse(record.device_errors)
            : record.device_errors;
        }
      } catch (_) {}
      const rows = Object.entries(errors).map(([device, msg]) => ({
        ts: record.ts ?? record.timestamp,
        device,
        msg,
      }));
      setErrorLogs(rows);
    } catch (_) {
      setErrorLogs([]);
    } finally {
      setErrorLoading(false);
    }
  }, [accessToken, edge?.edgeId]);

  const fetchAlarms = useCallback(async () => {
    if (!accessToken || !edgeDbId) return;
    setAlarmsLoading(true);
    try {
      const rows = await getEdgeAlarmEvents(accessToken, edgeDbId);
      setAlarms(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setAlarms([]);
    } finally {
      setAlarmsLoading(false);
    }
  }, [accessToken, edgeDbId]);

  const handleAckAlarm = async (eventId) => {
    try {
      await ackAlarmEvent(accessToken, edgeDbId, eventId);
      fetchAlarms();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("edgeDetail.detail.ackFailed"));
    }
  };

  useEffect(() => {
    if (activeTab === "sensor_latest") fetchSensorLatest();
    else if (activeTab === "error_logs") fetchErrorLogs();
    else if (activeTab === "alarms") fetchAlarms();
  }, [activeTab, fetchSensorLatest, fetchErrorLogs, fetchAlarms]);

  const handleSaveCloudTransfer = async () => {
    if (!edge || !canEditEdge) return;
    setSavingCloudTransfer(true);
    try {
      const updated = await setEdgeCloudTransfer(
        accessToken,
        edge.id,
        cloudTransferEnabled
      );
      setEdge(updated);
      setCloudTransferEnabled(Boolean(updated.cloud_transfer_enabled));
      toast.success(t("edgeDetail.detail.cloudSaved"));
    } catch (err) {
      setCloudTransferEnabled(Boolean(edge.cloud_transfer_enabled));
      toast.error(err.response?.data?.detail || t("edgeDetail.detail.cloudSaveFailed"));
    } finally {
      setSavingCloudTransfer(false);
    }
  };

  const handleSaveTier = async () => {
    if (!edge || !canChangeTier) return;
    if (!window.confirm(t("edgeDetail.detail.tierConfirm", { tier }))) return;
    setSavingTier(true);
    try {
      const updated = await changeEdgeTier(accessToken, edge.id, tier);
      setEdge(updated);
      setTier((updated.tier || "pro").toLowerCase() === "lite" ? "lite" : "pro");
      toast.success(t("edgeDetail.detail.tierSaved"));
    } catch (err) {
      setTier((edge.tier || "pro").toLowerCase() === "lite" ? "lite" : "pro");
      toast.error(apiErrorMessage(err, t("edgeDetail.detail.tierSaveFailed")));
    } finally {
      setSavingTier(false);
    }
  };

  if (loading) {
    return <div style={styles.container}>{t("edgeDetail.common.loading")}</div>;
  }


  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/edges")}>
{t("edgeDetail.detail.back")}        </button>
        <div>
          <h1 style={styles.title}>{edge.name ?? edge.edgeId}</h1>
          <p style={styles.subtitle}>
            {edge.asset.type} - {edge.asset.location}
          </p>
        </div>
        {canEditEdge && (
          <>
            <button style={styles.editBtn} onClick={() => setShowEditDialog(true)}>
{t("edgeDetail.detail.edit")}            </button>
            <button style={styles.editBtn} onClick={handleReclaim} disabled={claiming || !hasCap(me, "edge.claim")}>
              {claiming ? t("edgeDetail.detail.generating") : t("edgeDetail.detail.getClaim")}
            </button>
            <button style={styles.editBtn} onClick={() => setShowMoveDialog(true)}>
{t("edgeDetail.detail.moveLocation")}            </button>
            {canTransfer && (
              <button style={styles.editBtn} onClick={() => setShowTransferDialog(true)}>
{t("edgeDetail.detail.transfer")}              </button>
            )}
            <button style={styles.deleteBtn} onClick={handleDeleteEdge} disabled={deleting}>
              {deleting ? t("edgeDetail.detail.deleting") : t("edgeDetail.detail.delete")}
            </button>
          </>
        )}
        <span style={styles.statusBadge}>{t("edgeDetail.detail.active")}</span>
      </div>

      {/* Edge Info Cards */}
      <div style={styles.infoGrid}>
        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>{t("edgeDetail.detail.deviceInfo")}</h3>
          <p>
            <strong>{t("edgeDetail.detail.manufacturer")}</strong> {edge.device.manufacturer}
          </p>
          <p>
            <strong>{t("edgeDetail.detail.model")}</strong> {edge.device.model}
          </p>
          <p>
            <strong>{t("edgeDetail.detail.firmware")}</strong> {edge.device.firmware}
          </p>
          <p>
            <strong>{t("edgeDetail.detail.osVersion")}</strong> {edge.reported_os_version || "—"}
          </p>
          <p>
            <strong>{t("edgeDetail.detail.cloud")}</strong> {edge.is_online ? t("edgeDetail.detail.online") : t("edgeDetail.detail.offline")}
          </p>
          <p>
            <strong>VPN:</strong>{" "}
            {{ connected: t("edgeDetail.detail.vpnConnected"), retrying: t("edgeDetail.detail.vpnRetrying"), offline: t("edgeDetail.detail.vpnOffline") }[edge.vpn_status] || "—"}
            {edge.vpn_status && edge.vpn_status !== "connected" && edge.vpn_status_since
              ? t("edgeDetail.detail.vpnSince", { date: formatDate(new Date(edge.vpn_status_since + (edge.vpn_status_since.endsWith("Z") ? "" : "Z")), STAMP) })
              : ""}
          </p>
          <p>
            <strong>{t("edgeDetail.detail.otaChannel")}</strong> {edge.ota_channel || "stable"}
          </p>
        </div>
        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>{t("edges.mac.title")}</h3>
          {edge.valonix_serial && (
            <p>
              <strong>{t("edgeDetail.detail.labelSerial")}</strong> <span style={{ fontFamily: "monospace" }}>{edge.valonix_serial}</span>
            </p>
          )}
          <MacList edge={edge} />
        </div>
        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>{t("edgeDetail.detail.assetInfo")}</h3>
          <p>
            <strong>{t("edgeDetail.detail.assetId")}</strong> {edge.asset.assetId}
          </p>
          <p>
            <strong>{t("edgeDetail.detail.type")}</strong> {edge.asset.type}
          </p>
          <p>
            <strong>{t("edgeDetail.detail.location")}</strong> {edge.asset.location}
          </p>
        </div>
        {isSuper && (() => {
          const c = certInfo(edge);
          const col = { ok: "#276749", warn: "#975a16", off: "#c53030", none: "inherit" }[c.kind];
          return (
            <div style={styles.infoCard}>
              <h3 style={styles.infoTitle}>{t("edgeDetail.detail.certificate")}</h3>
              <p style={{ fontSize: "22px", fontWeight: 700, color: col }}>{c.text}</p>
              {c.sub && <p style={{ color: "var(--text-muted)", fontSize: "13px" }}>{c.sub}</p>}
            </div>
          );
        })()}
        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>{t("edgeDetail.detail.tierTitle")}</h3>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
            <input
              type="radio"
              name="edge-tier"
              value="lite"
              checked={tier === "lite"}
              disabled={!canChangeTier || savingTier}
              onChange={() => setTier("lite")}
            />
            <span>{t("edgeDetail.detail.tierLite")}</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            <input
              type="radio"
              name="edge-tier"
              value="pro"
              checked={tier === "pro"}
              disabled={!canChangeTier || savingTier}
              onChange={() => setTier("pro")}
            />
            <span>{t("edgeDetail.detail.tierPro")}</span>
          </label>
          <p style={{ color: "var(--text-muted)", fontSize: "12px" }}>
            {t("edgeDetail.detail.tierNote")}
          </p>
          {!canChangeTier && (
            <p style={{ color: "var(--text-muted)", fontSize: "12px" }}>
              {t("edgeDetail.detail.tierAssigned")}
            </p>
          )}
          {canChangeTier && (
            <button
              style={sysStyles.refreshBtn}
              disabled={savingTier || tier === ((edge.tier || "pro").toLowerCase() === "lite" ? "lite" : "pro")}
              onClick={handleSaveTier}
            >
              {savingTier ? t("edgeDetail.detail.saving") : t("edgeDetail.detail.saveTier")}
            </button>
          )}
        </div>
        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>{t("edgeDetail.detail.cloudTransfer")}</h3>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            <input
              type="checkbox"
              checked={cloudTransferEnabled}
              disabled={!canToggleCloudTransfer || savingCloudTransfer || tier === "lite"}
              onChange={(event) => setCloudTransferEnabled(event.target.checked)}
              aria-label={t("edgeDetail.detail.cloudAria")}
            />
            <span>{cloudTransferEnabled ? t("edgeDetail.detail.on") : t("edgeDetail.detail.off")}</span>
          </label>
          <p style={{ color: "var(--text-muted)", fontSize: "12px" }}>
            {t("edgeDetail.detail.cloudNote")}
          </p>
          {canEditEdge && !isSuper && !cloudTransferAllowed && (
            <p style={{ color: "var(--text-muted)", fontSize: "12px" }}>
              {t("edgeDetail.detail.cloudNotEnabled")}
            </p>
          )}
          {canToggleCloudTransfer && (
            <button
              style={sysStyles.refreshBtn}
              disabled={
                savingCloudTransfer ||
                tier === "lite" ||
                cloudTransferEnabled === Boolean(edge.cloud_transfer_enabled)
              }
              onClick={handleSaveCloudTransfer}
            >
              {savingCloudTransfer ? t("edgeDetail.detail.saving") : t("edgeDetail.detail.saveSetting")}
            </button>
          )}
        </div>
      </div>
      <div style={styles.tabs}>
        <button
          style={activeTab === "sensor_latest" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("sensor_latest")}
        >
          {t("edgeDetail.detail.tabSensor")}
        </button>
        <button
          style={activeTab === "error_logs" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("error_logs")}
        >
          {t("edgeDetail.detail.tabErrors")}
        </button>
        <button
          style={activeTab === "alarms" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("alarms")}
        >
          {t("edgeDetail.detail.tabAlarms")}{edge?.open_alarm_count > 0 ? ` (${edge.open_alarm_count})` : ""}
        </button>
        <button
          style={activeTab === "incidents" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("incidents")}
        >
          {t("edgeDetail.detail.tabIncidents")}
        </button>
        <button
          style={activeTab === "assets" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("assets")}
        >
          {t("edgeDetail.detail.tabAssets")}
        </button>
        {canEditEdge && (
          <button style={activeTab === "ota" ? styles.tabActive : styles.tab} onClick={() => setActiveTab("ota")}>
            OTA
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {activeTab === "sensor_latest" && (
          <SensorLatestTab rows={sensorRows} loading={sensorLoading} onRefresh={fetchSensorLatest} />
        )}
        {activeTab === "ota" && canEditEdge && <OtaCard edge={edge} />}
        {activeTab === "error_logs" && (
          <ErrorLogsTab logs={errorLogs} loading={errorLoading} onRefresh={fetchErrorLogs} />
        )}
        {activeTab === "alarms" && (
          <AlarmsTab alarms={alarms} loading={alarmsLoading} onRefresh={fetchAlarms} onAck={handleAckAlarm} />
        )}
        {activeTab === "incidents" && (
          <EdgeIncidentsTab accessToken={accessToken} edgeDbId={edgeDbId} canLabel={canEditEdge} />
        )}
        {activeTab === "assets" && <AssetsTab edgeId={edge.edgeId} accessToken={accessToken} />}
      </div>

      <EdgeEditDialog
        open={showEditDialog}
        edge={edge}
        accessToken={accessToken}
        onClose={() => setShowEditDialog(false)}
        onSaved={fetchEdge}
      />
      <TransferEdgeDialog
        open={showTransferDialog}
        edge={edge}
        accessToken={accessToken}
        onClose={() => setShowTransferDialog(false)}
        onTransferred={() => navigate("/edges")}
      />
      <MoveEdgeDialog
        open={showMoveDialog}
        edge={edge}
        accessToken={accessToken}
        onClose={() => setShowMoveDialog(false)}
        onSaved={fetchEdge}
      />
      <Dialog open={!!claim} onClose={() => setClaim(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } }}>
        <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{t("edgeDetail.detail.reclaimTitle")}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("edgeDetail.detail.claimWarn")}
          </Alert>
          <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 1 }}>{t("edgeDetail.detail.claimOnce")}</Typography>
          <div style={{ padding: 16, background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: 6, fontFamily: "monospace", wordBreak: "break-all" }}>
            {claim?.claim_code}
          </div>
          <Typography variant="caption" sx={{ color: "var(--text-muted)", mt: 1, display: "block" }}>
            {t("edgeDetail.detail.claimExpires", { date: claim?.expires_at ? formatDate(claim.expires_at, STAMP) : "—" })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
          <Button
            onClick={async () => {
              await navigator.clipboard.writeText(claim.claim_code);
              toast.success(t("edgeDetail.detail.claimCopied"));
            }}
            sx={{ color: "var(--accent-navy-light)" }}
          >
            {t("edgeDetail.detail.copy")}
          </Button>
          <Button onClick={() => setClaim(null)} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>{t("edgeDetail.detail.close")}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Alarmlar Tab — /metrics/alarms/{edgeId} (son 24 saat, en fazla 50 kayıt)
function AlarmsTab({ alarms, loading, onRefresh, onAck }) {
  const { t } = useTranslation();
  if (loading) return <div style={{ padding: "32px", color: "var(--text-muted)", textAlign: "center" }}>{t("edgeDetail.common.loading")}</div>;

  return (
    <div style={{ color: "var(--text-primary)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "var(--danger)" }}>
          {t("edgeDetail.detail.tabAlarms")}
          {alarms.length > 0 && (
            <span style={{
              marginLeft: "10px",
              backgroundColor: "rgba(197, 48, 48, 0.2)",
              border: "1px solid rgba(197, 48, 48, 0.4)",
              borderRadius: "10px",
              padding: "2px 8px",
              fontSize: "12px",
              color: "var(--danger)",
            }}>
              {alarms.length}
            </span>
          )}
        </h3>
        <button style={sysStyles.refreshBtn} onClick={onRefresh}>{t("edgeDetail.common.refresh")}</button>
      </div>
      {alarms.length === 0 ? (
        <div style={{ padding: "32px", textAlign: "center" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>✓</div>
          <div style={{ color: "var(--success)", fontSize: "15px", fontWeight: 600 }}>{t("edgeDetail.alarms.none")}</div>
          <div style={{ color: "var(--text-muted)", fontSize: "13px", marginTop: "4px" }}>{t("edgeDetail.alarms.noneHint")}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {alarms.map((a) => {
            const isOpen = a.status === "open";
            return (
              <div key={a.id} style={{
                display: "flex",
                gap: "16px",
                alignItems: "flex-start",
                backgroundColor: isOpen ? "rgba(197, 48, 48, 0.07)" : "var(--bg-hover)",
                border: `1px solid ${isOpen ? "rgba(197, 48, 48, 0.2)" : "var(--border)"}`,
                borderRadius: "8px",
                padding: "10px 14px",
              }}>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", minWidth: "140px" }}>
                  {a.created_at ? formatDate(a.created_at, STAMP) : "-"}
                </span>
                <span style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: isOpen ? "var(--danger)" : "var(--text-muted)",
                  textTransform: "uppercase",
                  minWidth: "60px",
                }}>
                  {isOpen ? t("edgeDetail.alarms.open") : a.status ?? "-"}
                </span>
                <span style={{ fontWeight: 600, minWidth: "140px" }} title={a.field ?? ""}>{alarmFieldLabel(a.field)}</span>
                <span style={{ flex: 1, color: "var(--text-secondary)" }}>
                  {a.value ?? "-"} {a.threshold != null && t("edgeDetail.alarms.threshold", { value: a.threshold })}
                </span>
                {isOpen && onAck && (
                  <button
                    style={{ ...sysStyles.refreshBtn, flexShrink: 0 }}
                    onClick={() => onAck(a.id)}
                  >
                    {t("edgeDetail.detail.close")}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const sysStyles = {
  refreshBtn: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    padding: "4px 12px",
    cursor: "pointer",
    fontSize: "12px",
  },
  metricCard: {
    backgroundColor: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    padding: "16px",
  },
  progressBg: {
    width: "100%",
    height: "8px",
    backgroundColor: "var(--bg-hover)",
    borderRadius: "4px",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.3s ease",
  },
  deviceSection: {
    backgroundColor: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    padding: "16px",
  },
  deviceStat: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "center",
    minWidth: "80px",
  },
  errorBadge: {
    backgroundColor: "rgba(197, 48, 48, 0.1)",
    border: "1px solid rgba(197, 48, 48, 0.3)",
    borderRadius: "6px",
    padding: "8px 12px",
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    fontSize: "13px",
    color: "var(--text-primary)",
  },
};

// Son Alınan Sensör Verileri Tab
function SensorLatestTab({ rows, loading, onRefresh }) {
  const { t } = useTranslation();
  if (loading) return <div style={{ padding: "32px", color: "var(--text-muted)", textAlign: "center" }}>{t("edgeDetail.common.loading")}</div>;

  return (
    <div style={{ color: "var(--text-primary)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "var(--accent-navy-light)" }}>{t("edgeDetail.detail.tabSensor")}</h3>
        <button style={sysStyles.refreshBtn} onClick={onRefresh}>{t("edgeDetail.common.refresh")}</button>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "32px", textAlign: "center", color: "var(--text-muted)" }}>
          {t("edgeDetail.sensor.empty")}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ backgroundColor: "var(--bg-surface)" }}>
                {["device", "tag", "value", "unit", "time"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "var(--text-muted)", borderBottom: "1px solid var(--border)", fontWeight: 600 }}>
                    {t(`edgeDetail.sensor.col.${h}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "9px 14px", color: "var(--text-primary)" }}>{r.device_name ?? r.device_id ?? "—"}</td>
                  <td style={{ padding: "9px 14px", color: "var(--accent-navy-light)" }}>{r.tag_label ?? r.tag_name ?? "—"}</td>
                  <td style={{ padding: "9px 14px", color: "var(--text-primary)", fontWeight: 600 }}>
                    {r.value != null ? Number(r.value).toFixed(3) : "—"}
                  </td>
                  <td style={{ padding: "9px 14px", color: "var(--text-muted)" }}>{r.unit ?? "—"}</td>
                  <td style={{ padding: "9px 14px", color: "var(--text-muted)", fontSize: "12px" }}>
                    {r.time ? formatDate(r.time, STAMP) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Hata Logları Tab
function ErrorLogsTab({ logs, loading, onRefresh }) {
  const { t } = useTranslation();
  if (loading) return <div style={{ padding: "32px", color: "var(--text-muted)", textAlign: "center" }}>{t("edgeDetail.common.loading")}</div>;

  return (
    <div style={{ color: "var(--text-primary)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "var(--danger)" }}>
          {t("edgeDetail.detail.tabErrors")}
          {logs.length > 0 && (
            <span style={{
              marginLeft: "10px",
              backgroundColor: "rgba(197, 48, 48, 0.2)",
              border: "1px solid rgba(197, 48, 48, 0.4)",
              borderRadius: "10px",
              padding: "2px 8px",
              fontSize: "12px",
              color: "var(--danger)",
            }}>
              {logs.length}
            </span>
          )}
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>{t("edgeDetail.errors.last24h")}</span>
          <button style={sysStyles.refreshBtn} onClick={onRefresh}>{t("edgeDetail.common.refresh")}</button>
        </div>
      </div>
      {logs.length === 0 ? (
        <div style={{ padding: "32px", textAlign: "center" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>✓</div>
          <div style={{ color: "var(--success)", fontSize: "15px", fontWeight: 600 }}>{t("edgeDetail.errors.none")}</div>
          <div style={{ color: "var(--text-muted)", fontSize: "13px", marginTop: "4px" }}>{t("edgeDetail.errors.noneHint")}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {logs.map((log, i) => (
            <div key={i} style={{
              display: "flex",
              gap: "16px",
              alignItems: "flex-start",
              backgroundColor: "rgba(197, 48, 48, 0.07)",
              border: "1px solid rgba(197, 48, 48, 0.25)",
              borderRadius: "8px",
              padding: "12px 16px",
            }}>
              <span style={{
                flexShrink: 0,
                backgroundColor: "rgba(197, 48, 48, 0.2)",
                border: "1px solid rgba(197, 48, 48, 0.4)",
                borderRadius: "6px",
                padding: "3px 10px",
                fontSize: "12px",
                color: "var(--danger)",
                fontWeight: 700,
              }}>
                {log.device}
              </span>
              <span style={{ flex: 1, fontSize: "13px", color: "var(--text-primary)", lineHeight: 1.5 }}>{log.msg}</span>
              <span style={{ flexShrink: 0, fontSize: "11px", color: "var(--text-muted)" }}>
                {log.ts ? formatDate(log.ts, STAMP) : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "24px",
    maxWidth: "1200px",
    margin: "0 auto",
    color: "var(--text-primary)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
  },
  backBtn: {
    padding: "8px 16px",
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    cursor: "pointer",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: 0,
    color: "var(--text-primary)",
  },
  subtitle: {
    color: "var(--text-muted)",
    margin: 0,
  },
  statusBadge: {
    marginLeft: "auto",
    backgroundColor: "var(--success)",
    color: "#fff",
    padding: "6px 16px",
    borderRadius: "16px",
    fontSize: "14px",
  },
  deleteBtn: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    color: "var(--danger)",
    border: "1px solid var(--danger)",
    borderRadius: "8px",
    cursor: "pointer",
  },
  editBtn: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    color: "var(--accent-navy-light)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    cursor: "pointer",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  infoCard: {
    backgroundColor: "var(--bg-surface)",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid var(--border)",
    color: "var(--text-primary)",
  },
  infoTitle: {
    fontSize: "14px",
    color: "var(--accent-navy-light)",
    textTransform: "uppercase",
    marginBottom: "12px",
  },
  tabs: {
    display: "flex",
    gap: "8px",
    borderBottom: "2px solid var(--border)",
    marginBottom: "24px",
  },
  tab: {
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    fontSize: "14px",
    color: "var(--text-muted)",
    marginBottom: "-2px",
  },
  tabActive: {
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "2px solid var(--accent-navy-light)",
    cursor: "pointer",
    fontSize: "14px",
    color: "var(--accent-navy-light)",
    fontWeight: "bold",
    marginBottom: "-2px",
  },
  tabContent: {
    backgroundColor: "var(--bg-surface)",
    padding: "24px",
    borderRadius: "12px",
    border: "1px solid var(--border)",
  },
  placeholder: {
    textAlign: "center",
    padding: "32px",
    color: "var(--text-primary)",
  },
  addBtn: {
    marginTop: "16px",
    padding: "12px 24px",
    backgroundColor: "var(--btn-primary)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
  },
};
